/**
 * LA PÓLIZA, TAL COMO LA ESPERA EL SERVIDOR.
 *
 * Vive fuera de `api.js` desde el 2026-09-13 para poder PROBARSE: `api.js`
 * arrastra `AuthGate.jsx`, que necesita `window` y revienta fuera de un
 * navegador (y un `.jsx` no se importa desde una prueba — regla de oro 48).
 * Mismo motivo por el que ya viven aparte `policyStatuses.js`,
 * `marketplaceHousehold.js` o `anioDePoliza.js`.
 *
 * ── LA TRAMPA QUE SE ARREGLA AQUÍ, Y QUE COSTÓ DINERO ──────────────────────
 *
 * Lissette, 2026-09-13, mirando el historial: *"le cambié la prima a una
 * póliza y mira, tenía 55 de prima y la cambié a 45 y me dice que tenía 0"*.
 * Y tenía razón: la póliza valía 0 de verdad en ese momento.
 *
 * Este objeto se arma con TODOS los campos siempre, poniendo un valor por
 * defecto cuando el que llega no los trae. Para CREAR está bien —el servidor
 * los exige—, pero el mismo armador se usa para GUARDAR UN CAMBIO, y ahí un
 * objeto que no traiga la prima mandaba `premium: 0`. El servidor no puede
 * distinguir "ponla en cero" de "no la mencioné": escribe el cero.
 *
 * Así que cualquier guardado parcial de una póliza —una etiqueta, un estatus,
 * una comisión, una casilla— podía **poner la prima en cero sin decir nada**.
 * Y no deja rastro reconocible: el historial apunta el cambio de dinero
 * mezclado con lo que la persona sí quería tocar, o ni eso.
 *
 * EL ARCHIVO YA SE SABÍA LA LECCIÓN. Cuatro campos —`leadType`, `agentLabel`,
 * `processedBy`, `processingDate`— llevaban el candado `!== undefined` con un
 * comentario que dice, literalmente, *"un guardado parcial las borraría todas
 * — la misma trampa de driveUrl"*. Se arregló para esos cuatro y no para el
 * resto. Un arreglo que se aplica solo al campo que se reportó deja a los
 * hermanos esperando su turno; esta vez se cierra la clase entera.
 *
 * ── CÓMO QUEDA ────────────────────────────────────────────────────────────
 *
 * `paraCrear: true`  → como siempre: los obligatorios van con su valor por
 *                      defecto, porque el servidor no acepta la póliza sin
 *                      ellos.
 * `paraCrear` falso  → **solo viaja lo que el objeto trae**. Una clave ausente
 *                      no se manda, y el servidor deja ese campo como estaba.
 *
 * La diferencia entre "no lo mencioné" y "ponlo vacío" se decide por si la
 * CLAVE existe, no por si el valor es falsy: `monthlyPremium: ""` sigue
 * mandando 0 —vaciar la casilla a mano es una decisión— y `monthlyPremium`
 * ausente ya no manda nada.
 */
import { estadoParaElServidor } from './policyStatuses.js';
import { TIPO_A_ENUM as POLICY_TYPE_FRONT_TO_BACK } from './policyTypes.js';
import { POLICY_DETAIL_KEYS } from './camposDePoliza.js';
import { importeDeTexto } from './dineroDeTexto.js';

/** ¿El objeto MENCIONA esta clave? (Aunque venga vacía.) */
const trae = (p, k) => !!p && Object.prototype.hasOwnProperty.call(p, k);

/** Manda `{ clave: valor }` solo si el objeto menciona `origen`. */
const soloSiTrae = (p, origen, clave, valor) => (trae(p, origen) ? { [clave]: valor } : {});

export function mapPolicyToApi(p, { paraCrear = false } = {}) {
  const q = p || {};
  // Al CREAR, el servidor exige tipo, aseguradora, estatus, fecha efectiva y
  // prima: si no vienen se mandan con su valor por defecto, como siempre.
  const obligatorio = (origen, clave, valor) =>
    (paraCrear || trae(q, origen) ? { [clave]: valor } : {});
  return {
    // El cliente NO se manda en un guardado parcial: mover una poliza de dueno
    // arrastra sus comisiones y sus avisos, y es una operacion aparte (E-229).
    ...soloSiTrae(q, 'clientId', 'clientId', q.clientId),
    // Ver la nota gemela en mapClientToApi: una póliza importada no abre
    // tarjeta de Seguimiento.
    ...(q.skipOnboarding ? { skipOnboarding: true } : {}),
    // Una clave por "Guardar" del formulario: si llega dos veces, el servidor
    // devuelve la poliza ya creada en vez de otra (2026-10-20, E-374).
    ...(paraCrear && q.claveDeCreacion ? { claveDeCreacion: String(q.claveDeCreacion) } : {}),
    ...obligatorio('type', 'type', POLICY_TYPE_FRONT_TO_BACK[q.type] || 'HEALTH'),
    ...obligatorio('carrier', 'carrier', q.carrier || 'Unknown'),
    ...soloSiTrae(q, 'plan', 'planName', q.plan || null),
    ...soloSiTrae(q, 'policyNumber', 'policyNumber', q.policyNumber || null),
    ...soloSiTrae(q, 'memberId', 'memberId', q.memberId || null),
    ...soloSiTrae(q, 'memberIdsByPerson', 'memberIdsByPerson',
      (q.memberIdsByPerson && typeof q.memberIdsByPerson === 'object') ? q.memberIdsByPerson : {}),
    // Las dos columnas del estatus salen de UNA sola regla, en
    // policyStatuses.js, porque el cambio EN LOTE manda las mismas dos.
    ...(paraCrear || trae(q, 'status') ? estadoParaElServidor(q.status) : {}),
    ...obligatorio('effectiveDate', 'effectiveDate',
      q.effectiveDate || new Date().toISOString().split('T')[0]),
    ...soloSiTrae(q, 'renewalDate', 'expirationDate', q.renewalDate || null),
    ...soloSiTrae(q, 'cancellationDate', 'cancellationDate', q.cancellationDate || null),
    // LA PRIMA: ver la cabecera. Ausente no viaja; vacia a mano si (es cero).
    ...obligatorio('monthlyPremium', 'premium',
      q.monthlyPremium ? Number(String(q.monthlyPremium).replace(/[^0-9.]/g, '')) || 0 : 0),
    ...(paraCrear ? { premiumFrequency: 'MONTHLY' } : {}),
    ...soloSiTrae(q, 'coverageAmount', 'coverageAmount', q.coverageAmount || null),
    // El deducible es una columna DECIMAL y el servidor rechaza "1,000" o
    // "$2,500" (400, "Must be a number") — justo como lo sugieren los
    // ejemplos del formulario. Se limpia aquí; si no es una cifra se manda tal
    // cual para que el servidor lo diga, en vez de borrarlo en silencio.
    ...soloSiTrae(q, 'deductible', 'deductible',
      q.deductible ? (importeDeTexto(q.deductible) ?? q.deductible) : null),
    ...soloSiTrae(q, 'beneficiary', 'beneficiary', q.beneficiary || null),
    ...soloSiTrae(q, 'notes', 'notes', q.notes || null),
    // Only send the insured selection when the form actually carries one
    // (always an array once a PolicyForm has mounted). Leaving it undefined
    // omits it from the request so an existing selection isn't clobbered.
    policyInsured: Array.isArray(q.policyInsured) ? q.policyInsured : undefined,
    policyInsuredExtra: Array.isArray(q.policyInsuredExtra)
      ? q.policyInsuredExtra.map((s) => String(s).trim()).filter(Boolean)
      : undefined,
    // Policy-card classification. Send only when present so we never clobber an
    // existing value with undefined on a partial update.
    leadType: q.leadType !== undefined ? (q.leadType || null) : undefined,
    agentLabel: q.agentLabel !== undefined ? (q.agentLabel || null) : undefined,
    processedBy: q.processedBy !== undefined ? (q.processedBy || null) : undefined,
    processingDate: q.processingDate !== undefined ? (q.processingDate || null) : undefined,
    // Only send a clean array of non-empty {label,value} pairs.
    // The label is clamped to the SAME 60 chars the backend enforces
    // (policies.routes.ts: customFields[].label.max(60)). Without the clamp an
    // over-long name reached the API and the whole save died with "Label:
    // String must contain at most 60 character(s)" — and because these names
    // come from the agency-shared catalog for the policy type, ONE bad name
    // blocked saving EVERY policy of that type, naming a field the agent
    // couldn't see (rows removed with the X stay in the form as `hidden`, are
    // skipped by the renderer, and were still submitted). Maricarmen Nieto /
    // Pomares, 2026-08-07 — she saw only "PCP" and "NPN" on screen.
    // Clamping here also SELF-HEALS an agency whose stored catalog is already
    // poisoned: the save goes through, and PoliciesView then rewrites the
    // catalog with the clamped names.
    customFields: Array.isArray(q.customFields)
      ? q.customFields
          .map((f) => ({
            label: String(f?.label || '').trim().slice(0, 60),
            value: String(f?.value || '').trim(),
          }))
          .filter((f) => f.label || f.value)
      : undefined,
    // Etiquetas de PÓLIZA. Solo se mandan cuando el formulario las trae: una
    // clave ausente deja intactas las guardadas, y `[]` las quita a propósito.
    // Sin ese `!== undefined`, un guardado parcial (una pantalla que no pinta
    // las etiquetas) las borraría todas — la misma trampa de driveUrl.
    tags: Array.isArray(q.tags)
      ? q.tags.map((t) => String(t || '').trim().slice(0, 40)).filter(Boolean)
      : undefined,
    // Pack the type-specific fields (no dedicated columns) into one JSON blob so
    // nothing the form collects is dropped. Omit when empty to avoid clobbering.
    typeDetails: (() => {
      const d = {};
      for (const k of POLICY_DETAIL_KEYS) {
        const v = q[k];
        if (v !== undefined && v !== null && v !== '') d[k] = v;
      }
      // Stamp the exact front-end policy type so round-trip-lossy types
      // (partd/suppl/referred → HEALTH, final → LIFE) restore to their true
      // sub-type on read instead of collapsing to a sibling. Self-healing:
      // re-stamped from the current type on every save. Additive — legacy rows
      // without it fall back to the enum mapping in mapPolicyFromApi.
      if (q.type) d.frontType = p.type;
      return Object.keys(d).length ? d : undefined;
    })(),
  };
}
